import { Client } from '../types';

/**
 * Check if a client has all cases closed
 */
export const hasAllCasesClosed = (client: Client): boolean => {
  if (client.cases.length === 0) return false;
  return client.cases.every(caseItem => caseItem.status === 'Closed');
};

/**
 * Check if a client has cleared all balances
 */
export const hasBalanceCleared = (client: Client): boolean => {
  const totalBalance = client.cases.reduce((sum, caseItem) => {
    const totalPaid = caseItem.payments.reduce((pSum, payment) => pSum + (Number(payment.amount) || 0), 0);
    return sum + (caseItem.amountCharged - totalPaid);
  }, 0);
  
  return totalBalance <= 0;
};

/**
 * Check if a client is eligible for archiving
 */
export const isEligibleForArchiving = (client: Client): { 
  eligible: boolean; 
  reasons: string[];
  suggestedReason: 'cases_closed' | 'balance_cleared' | 'manual' | null;
} => {
  const reasons: string[] = [];
  let suggestedReason: 'cases_closed' | 'balance_cleared' | 'manual' | null = null;
  
  const allCasesClosed = hasAllCasesClosed(client);
  const balanceCleared = hasBalanceCleared(client);
  
  if (allCasesClosed) {
    reasons.push('All cases are closed');
    suggestedReason = 'cases_closed';
  }
  
  if (balanceCleared) {
    reasons.push('Balance has been cleared');
    if (!suggestedReason) {
      suggestedReason = 'balance_cleared';
    }
  }
  
  return {
    eligible: reasons.length > 0,
    reasons,
    suggestedReason,
  };
};

/**
 * Get client's total balance owed
 */
export const getClientBalance = (client: Client): number => {
  return client.cases.reduce((sum, caseItem) => {
    const totalPaid = caseItem.payments.reduce((pSum, payment) => pSum + (Number(payment.amount) || 0), 0);
    return sum + (caseItem.amountCharged - totalPaid);
  }, 0);
};

/**
 * Get archive reason display text
 */
export const getArchiveReasonText = (reason: string): string => {
  switch (reason) {
    case 'cases_closed':
      return 'All Cases Closed';
    case 'balance_cleared':
      return 'Balance Cleared';
    case 'manual':
      return 'Manually Archived';
    case 'inactive':
      return 'Inactive Client';
    default:
      return 'Archived';
  }
};

/**
 * Get archive statistics
 */
export const getArchiveStats = (clients: Client[]) => {
  const total = clients.length;
  const archived = clients.filter(client => client.isArchived).length;
  const active = total - archived;
  const eligibleForArchive = clients.filter(client => 
    !client.isArchived && isEligibleForArchiving(client).eligible
  ).length;
  
  return {
    total,
    active,
    archived,
    eligibleForArchive,
    archivedPercentage: total > 0 ? Math.round((archived / total) * 100) : 0,
  };
};
